import { readdirSync, readFileSync } from "fs";
import { basename, join, relative } from "path";

export type UnsafeDbAuditIssueType = "unsafe-db" | "raw-sql" | "system-table";

export interface UnsafeDbAuditIssue {
  file: string;
  line: number;
  snippet: string;
  type: UnsafeDbAuditIssueType;
  operation?: string;
  allowlisted: boolean;
  blocked: boolean;
  reason: string;
}

export interface UnsafeDbSeedWarning {
  file: string;
  reason: string;
}

export interface UnsafeDbAuditResult {
  usages: { file: string; line: number; snippet: string }[];
  issues: UnsafeDbAuditIssue[];
  seedWarnings: UnsafeDbSeedWarning[];
  blocked: boolean;
}

interface PublicOperationRange {
  kind: string;
  startLine: number;
  endLine: number;
}

const ALLOW_COMMENT = "gencow-allow-unsafe-db";
const SYSTEM_TABLE_PATTERN = /\b_(?:system|gencow)_[a-zA-Z0-9_]*\b/;

const UNSAFE_PATTERNS: { type: UnsafeDbAuditIssueType; pattern: RegExp; reason: string }[] = [
  {
    type: "unsafe-db",
    pattern: /\b(?:ctx\.)?unsafeDb\b/,
    reason: "ctx.unsafeDb uses the audited system-plane database handle",
  },
  {
    type: "raw-sql",
    pattern: /\brawSql\b|\bSQL\.unsafe\b|\bclient\.unsafe\b|(?:^|[^\w])\.unsafe\s*\(/,
    reason: "raw SQL escape hatches need explicit review",
  },
  {
    type: "system-table",
    pattern: SYSTEM_TABLE_PATTERN,
    reason: "_system_* and _gencow_* tables are framework-owned",
  },
];

export function auditUnsafeDbUsage(functionsDir: string): UnsafeDbAuditResult {
  const issues: UnsafeDbAuditIssue[] = [];
  const seedWarnings: UnsafeDbSeedWarning[] = [];

  for (const file of findSourceFiles(functionsDir)) {
    const content = readFileSync(file, "utf-8");
    const relPath = relative(functionsDir, file);
    if (isSeedFile(relPath)) {
      if (UNSAFE_PATTERNS.some((unsafePattern) => unsafePattern.pattern.test(maskComments(content)))) {
        seedWarnings.push({
          file: relPath,
          reason: "seed.ts may use unsafe database access for seeding, but it must not be shipped or used in production",
        });
      }
      continue;
    }
    const operations = findPublicOperationRanges(content);
    const executableLines = maskComments(content).split("\n");
    const originalLines = content.split("\n");

    for (let index = 0; index < executableLines.length; index++) {
      const executableLine = executableLines[index];
      if (!executableLine.trim()) continue;

      for (const unsafePattern of UNSAFE_PATTERNS) {
        if (!unsafePattern.pattern.test(executableLine)) continue;

        const line = index + 1;
        const operation = operations.find((range) => range.startLine <= line && line <= range.endLine);
        const allowlisted = hasCompleteAllowComment(originalLines, index);
        const blocked = isBlockedIssue(unsafePattern.type, operation, allowlisted);

        issues.push({
          file: relPath,
          line,
          snippet: originalLines[index].trim().slice(0, 160),
          type: unsafePattern.type,
          operation: operation?.kind,
          allowlisted,
          blocked,
          reason: blocked ? buildBlockedReason(unsafePattern.type, operation) : unsafePattern.reason,
        });
      }
    }
  }

  return {
    usages: issues.map((issue) => ({ file: issue.file, line: issue.line, snippet: issue.snippet })),
    issues,
    seedWarnings,
    blocked: issues.some((issue) => issue.blocked),
  };
}

export function printUnsafeDbReport(result: UnsafeDbAuditResult): void {
  if (result.issues.length === 0 && result.seedWarnings.length === 0) return;

  const blocked = result.issues.filter((issue) => issue.blocked);
  const warnings = result.issues.filter((issue) => !issue.blocked);
  const log = blocked.length > 0 ? console.error : console.warn;

  if (result.issues.length > 0) {
    log(`\n[auditor] ⚠️  unsafe/raw database access detected in ${result.issues.length} location(s):`);
  }
  for (const issue of result.issues) {
    const marker = issue.blocked ? "✗" : issue.allowlisted ? "✓ allowlisted" : "⚠ review";
    const operation = issue.operation ? ` public ${issue.operation}` : " protected/internal code";
    log(`[auditor]   ${marker} ${issue.file}:${issue.line} — ${issue.type}${operation}`);
    log(`[auditor]     ${issue.snippet}`);
    log(`[auditor]     → ${issue.reason}`);
  }
  for (const seedWarning of result.seedWarnings) {
    log(`\n[auditor] ⚠️  ${seedWarning.file} is a seeding-only file`);
    log(`[auditor]     → ${seedWarning.reason}`);
  }
  if (blocked.length > 0) {
    log("[auditor]   → public handlers must use ctx.db, scoped helpers, or a complete allow comment.");
    log(
      `[auditor]   → allow comment format: // ${ALLOW_COMMENT} reason: ... scope: ... owner: ... test: ...`,
    );
  } else if (warnings.length > 0 || result.seedWarnings.length > 0) {
    log("[auditor]   → keep these reviewed and prefer ctx.db unless this is explicit infra/admin code.");
  }
  log("");
}

export interface DataIsolationAuditResult {
  passed: boolean;
  pgTablesWithUserId: string[];
  unsafeDbUsages: number;
}

export function auditDataIsolation(schemaPath: string, functionsDir: string): DataIsolationAuditResult {
  const pgTablesWithUserId: string[] = [];

  try {
    const content = readFileSync(schemaPath, "utf-8");
    const systemTables = new Set([
      "user",
      "session",
      "account",
      "verification",
      "_system_files",
      "_system_settings",
      "usage_snapshots",
      "credits",
    ]);
    const tableRegex =
      /export\s+const\s+(\w+)\s*=\s*pgTable\s*\(\s*"([^"]+)"\s*,\s*\{([^}]+(?:\{[^}]*\}[^}]*)*)\}/g;
    let match: RegExpExecArray | null;
    while ((match = tableRegex.exec(content)) !== null) {
      const [, , tableName, body] = match;
      if (systemTables.has(tableName)) continue;
      if (/user_id|userId/.test(body)) pgTablesWithUserId.push(tableName);
    }
  } catch {
    // optional schema file
  }

  const unsafeResult = auditUnsafeDbUsage(functionsDir);
  return {
    passed: pgTablesWithUserId.length === 0 && !unsafeResult.blocked,
    pgTablesWithUserId,
    unsafeDbUsages: unsafeResult.issues.length,
  };
}

export function printDataIsolationReport(result: DataIsolationAuditResult): void {
  if (result.passed && result.unsafeDbUsages === 0) {
    console.log("[auditor] ✅ Data isolation audit passed");
    return;
  }

  if (result.pgTablesWithUserId.length > 0) {
    console.error(`\n╔══════════════════════════════════════════════════════════════╗`);
    console.error(`║  ⛔ DEPLOY BLOCKED — pgTable without access control          ║`);
    console.error(`╚══════════════════════════════════════════════════════════════╝`);
    console.error(`\n  The following tables use pgTable() with userId but no access control:\n`);
    for (const table of result.pgTablesWithUserId) {
      console.error(`    ✗  "${table}" — add pgPolicy with ownerRls()`);
    }
    console.error(`\n  Fix: Add pgPolicy to your pgTable():`);
    console.error(`    import { ownerRls } from "@gencow/core";`);
    console.error(`    export const tasks = pgTable("tasks", { ... }, (t) => ownerRls(t.userId));\n`);
  }

  if (result.unsafeDbUsages > 0) {
    console.warn(`[auditor] ⚠️  ${result.unsafeDbUsages} unsafe/raw database access issue(s) found`);
  }
}

function isBlockedIssue(
  type: UnsafeDbAuditIssueType,
  operation: PublicOperationRange | undefined,
  allowlisted: boolean,
): boolean {
  if (!operation) return false;
  if (type === "system-table") return true;
  return !allowlisted;
}

function buildBlockedReason(
  type: UnsafeDbAuditIssueType,
  operation: PublicOperationRange | undefined,
): string {
  if (type === "system-table") {
    return `public ${operation?.kind ?? "handler"} must not expose framework-owned system tables`;
  }
  return `public ${operation?.kind ?? "handler"} uses an unreviewed database escape hatch`;
}

function hasCompleteAllowComment(lines: string[], lineIndex: number): boolean {
  const start = Math.max(0, lineIndex - 4);
  const nearby = lines.slice(start, lineIndex + 1).join("\n");
  return (
    nearby.includes(ALLOW_COMMENT) &&
    /\breason\s*:/i.test(nearby) &&
    /\bscope\s*:/i.test(nearby) &&
    /\bowner\s*:/i.test(nearby) &&
    /\btest\s*:/i.test(nearby)
  );
}

function findPublicOperationRanges(content: string): PublicOperationRange[] {
  const masked = maskCommentsAndStrings(content);
  const ranges: PublicOperationRange[] = [];

  // Legacy call-style: query(...), mutation(...), httpAction({ public: true, ... }).
  const operationPattern = /\b(query|mutation|httpAction)\s*\(/g;
  let match: RegExpExecArray | null;

  while ((match = operationPattern.exec(masked)) !== null) {
    const openParen = masked.indexOf("(", match.index);
    const closeParen = findMatchingParen(masked, openParen);
    if (closeParen === -1) continue;

    const block = masked.slice(match.index, closeParen + 1);
    if (!/\bpublic\s*:\s*true\b/.test(block)) continue;

    ranges.push({
      kind: match[1],
      startLine: lineNumberAt(content, match.index),
      endLine: lineNumberAt(content, closeParen),
    });
  }

  // New builder: httpRoute.<verb>.path(...).allowAnonymous().handler(...).
  // Treated as "public" iff `.allowAnonymous()` appears anywhere in the chain up to .handler()'s close.
  const httpRoutePattern = /\bhttpRoute\s*\.\s*(get|post|put|delete|patch)\b/g;
  let cursor = 0;
  while ((match = httpRoutePattern.exec(masked)) !== null) {
    if (match.index < cursor) continue;
    const handlerIdx = masked.indexOf(".handler", match.index);
    if (handlerIdx === -1) continue;
    const openParen = masked.indexOf("(", handlerIdx);
    if (openParen === -1) continue;
    const closeParen = findMatchingParen(masked, openParen);
    if (closeParen === -1) continue;

    const block = masked.slice(match.index, closeParen + 1);
    cursor = closeParen + 1;
    if (!/\.allowAnonymous\s*\(\s*\)/.test(block)) continue;

    ranges.push({
      kind: "httpRoute",
      startLine: lineNumberAt(content, match.index),
      endLine: lineNumberAt(content, closeParen),
    });
  }

  return ranges;
}

function findMatchingParen(content: string, openParen: number): number {
  let depth = 0;
  for (let index = openParen; index < content.length; index++) {
    const ch = content[index];
    if (ch === "(") depth++;
    if (ch === ")") {
      depth--;
      if (depth === 0) return index;
    }
  }
  return -1;
}

function lineNumberAt(content: string, index: number): number {
  let line = 1;
  for (let cursor = 0; cursor < index; cursor++) {
    if (content[cursor] === "\n") line++;
  }
  return line;
}

function findSourceFiles(dir: string): string[] {
  const results: string[] = [];
  try {
    for (const entry of readdirSync(dir, { withFileTypes: true })) {
      if (entry.name === "node_modules" || entry.name.startsWith(".")) continue;
      const fullPath = join(dir, entry.name);
      if (entry.isDirectory()) results.push(...findSourceFiles(fullPath));
      if (entry.isFile() && /\.(?:[cm]?[jt]sx?)$/.test(basename(entry.name))) results.push(fullPath);
    }
  } catch {
    // missing functions dir
  }
  return results;
}

function isSeedFile(file: string): boolean {
  return basename(file) === "seed.ts" || basename(file) === "seed.js";
}

function maskComments(content: string): string {
  let output = "";
  let inBlock = false;
  for (let i = 0; i < content.length; i++) {
    const ch = content[i];
    const next = content[i + 1];
    if (inBlock) {
      if (ch === "*" && next === "/") {
        output += "  ";
        i++;
        inBlock = false;
      } else {
        output += ch === "\n" ? "\n" : " ";
      }
      continue;
    }
    if (ch === "/" && next === "*") {
      output += "  ";
      i++;
      inBlock = true;
      continue;
    }
    if (ch === "/" && next === "/") {
      while (i < content.length && content[i] !== "\n") {
        output += " ";
        i++;
      }
      if (i < content.length) output += "\n";
      continue;
    }
    output += ch;
  }
  return output;
}

function maskCommentsAndStrings(content: string): string {
  const withoutComments = maskComments(content);
  let output = "";
  let quote: string | null = null;
  let escaped = false;

  for (const ch of withoutComments) {
    if (quote) {
      output += ch === "\n" ? "\n" : " ";
      if (!escaped && ch === quote) quote = null;
      escaped = !escaped && ch === "\\";
      if (ch !== "\\") escaped = false;
      continue;
    }

    if (ch === "'" || ch === '"' || ch === "`") {
      quote = ch;
      output += " ";
      continue;
    }

    output += ch;
  }

  return output;
}
